<template>
  <el-drawer
    :visible.sync="show"
    :with-header="false"
    size="1080px"
    append-to-body
    custom-class="vtm-aisles-drawer"
    @open="openDrawer"
    @closed="onClosed"
  >
    <div class="aisles" v-loading="loading" :element-loading-text="$t('common.loading')">
      <div class="aisles__head">
        <div class="aisles__head-main">
          <div class="aisles__title-row">
            <span class="aisles__title">{{ $t("aisles.title") }}</span>
            <span class="aisles__machine">{{ userEquipment.equipmentName }}</span>
            <span class="aisles__no vtm-mono">{{ userEquipment.equipmentNo }}</span>
          </div>
          <div class="aisles__subtitle">{{ $t("aisles.subtitle") }}</div>
        </div>
        <button type="button" class="aisles__close" @click="requestClose"><i class="el-icon-close"></i></button>
      </div>

      <div class="aisles__bar">
        <div class="aisles__chips">
          <button
            v-for="chip in chips"
            :key="chip.key"
            type="button"
            class="aisles-chip"
            :class="{ 'aisles-chip--active': activeChip === chip.key }"
            @click="toggleChip(chip.key)"
          >
            <span class="aisles-chip__count vtm-num">{{ chip.count }}</span>
            <span class="aisles-chip__label">{{ chip.label }}</span>
          </button>
        </div>
        <el-input
          v-model="search"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          class="aisles__search"
          :placeholder="$t('aisles.searchPh')"
        ></el-input>
      </div>

      <div class="aisles__toolbar">
        <el-radio-group v-model="view" size="small" @change="rememberView">
          <el-radio-button label="restock">{{ $t("aisles.viewRestock") }}</el-radio-button>
          <el-radio-button label="table">{{ $t("aisles.viewTable") }}</el-radio-button>
        </el-radio-group>
        <div class="aisles__bulk">
          <el-button size="small" plain icon="el-icon-upload2" @click="fillAll">{{ $t("aisles.fillAll") }}</el-button>
          <el-button size="small" plain @click="fillLow">{{ $t("aisles.fillLow") }}</el-button>
          <el-button size="small" plain @click="allPaste">{{ $t("userMachineLineProduct.allPaste") }}</el-button>
          <el-popover placement="bottom-end" width="360" trigger="click" popper-class="aisles-picklist-pop">
            <div class="aisles-picklist">
              <div class="aisles-picklist__head">
                <span class="aisles-picklist__title">{{ $t("aisles.pickList") }}</span>
                <el-button type="text" size="mini" :disabled="!pickListItems.length" @click="copyPickList">{{
                  $t("aisles.pickListCopy")
                }}</el-button>
              </div>
              <div class="aisles-picklist__hint">{{ $t("aisles.pickListHint") }}</div>
              <div v-if="!pickListItems.length" class="aisles-picklist__empty">{{ $t("aisles.pickListEmpty") }}</div>
              <ul v-else class="aisles-picklist__list">
                <li v-for="item in pickListItems" :key="item.name" class="aisles-picklist__item">
                  <span class="aisles-picklist__name">{{ item.name }}</span>
                  <span class="aisles-picklist__units vtm-num">{{ $t("aisles.unitsNeeded", { n: item.units }) }}</span>
                  <span class="aisles-picklist__slots vtm-mono">{{ slotsLabel(item) }}</span>
                </li>
              </ul>
            </div>
            <el-button slot="reference" size="small" plain icon="el-icon-tickets">{{ $t("aisles.pickList") }}</el-button>
          </el-popover>
          <el-button size="small" plain :loading="exportLoading" @click="handlerExportLine">Export</el-button>
        </div>
      </div>
      <div v-if="activeChip !== 'all' || search" class="aisles__scope-note">{{ $t("aisles.scopeNote") }}</div>

      <div class="aisles__content" :class="{ 'aisles__content--table': view !== 'restock' && rows.length > 0 }">
        <div v-if="!loading && rows.length === 0" class="aisles__empty">
          <i class="el-icon-box"></i>
          <span>{{ $t("aisles.empty") }}</span>
        </div>
        <template v-else>
          <div v-if="view === 'restock'" class="aisles__grid">
            <!-- 螺旋弹簧货道机：货道为平铺编号，按货道号平铺展示（无货架分组） -->
            <div class="aisles-shelf__row">
                <el-popover
                  v-for="row in sortedRows"
                  :key="row.id || row.aisleNo"
                  :ref="'pop_' + rowKey(row)"
                  placement="bottom"
                  width="360"
                  trigger="click"
                  popper-class="aisles-editor-pop"
                >
                  <div class="aisles-editor">
                    <div class="aisles-editor__head">
                      <span class="aisles-editor__title">{{ $t("aisles.editSlot", { n: row.aisleNo }) }}</span>
                      <span class="aisles-editor__actions">
                        <el-button type="text" size="mini" :disabled="!row.goodsId" @click="copy(row)">{{
                          $t("userMachineLineProduct.copy")
                        }}</el-button>
                        <el-button type="text" size="mini" @click="paste(row)">{{
                          $t("userMachineLineProduct.paste")
                        }}</el-button>
                      </span>
                    </div>
                    <div class="aisles-editor__field">
                      <label class="aisles-editor__label">{{ $t("userMachineLineProduct.goodsId") }}</label>
                      <el-select
                        v-model="row.goodsId"
                        style="width: 100%"
                        clearable
                        filterable
                        remote
                        :no-data-text="$t('common.noData')"
                        :no-match-text="$t('common.noData')"
                        :remote-method="handleFilter"
                        :placeholder="$t('userMachineLineProduct.select')"
                        v-el-select-loadmore="loadMore"
                        v-remove-readonly
                        @focus="onSelectFocus(row)"
                        @visible-change="onSelectVisible"
                        @clear="clearRow(row)"
                        @change="productChange(row)"
                      >
                        <el-option v-for="item in allGoodsList" :key="item.id" :label="item.name" :value="item.id"></el-option>
                        <el-option v-if="goodsLoading" disabled class="loading-option">
                          <span class="loading-text"><i class="el-icon-loading"></i> loading...</span>
                        </el-option>
                        <!-- 商品库为空时下拉里只有"暂无数据"，没有任何新建入口 -->
                        <div slot="empty" class="aisles-editor__noproducts">
                          <div class="aisles-editor__noproducts-text">{{ $t("common.noData") }}</div>
                          <el-button type="text" size="mini" icon="el-icon-plus" @click="openAddProduct">
                            {{ $t("addProduct.add") }}
                          </el-button>
                        </div>
                      </el-select>
                    </div>
                    <div v-if="row.url" class="aisles-editor__preview">
                      <el-image :src="row.url" fit="scale-down"></el-image>
                    </div>
                    <div class="aisles-editor__row">
                      <div class="aisles-editor__field">
                        <label class="aisles-editor__label">{{ $t("userMachineLineProduct.productPrice") }}</label>
                        <div class="aisles-editor__static vtm-num">{{ row.costPrice || 0 }}</div>
                      </div>
                      <div class="aisles-editor__field">
                        <label class="aisles-editor__label">{{ $t("userMachineLineProduct.sellingPrice") }}</label>
                        <el-input
                          v-model.lazy="row.sellingPrice"
                          size="small"
                          :disabled="!row.goodsId"
                          @blur="checkSellingPrice(row)"
                        ></el-input>
                      </div>
                    </div>
                    <div class="aisles-editor__row" v-if="showVat || showAge">
                      <div class="aisles-editor__field" v-if="showVat">
                        <label class="aisles-editor__label">{{ $t("userMachineLineProduct.vatRateSet") }}</label>
                        <el-input v-model.lazy="row.vatRate" size="small" type="number" :disabled="!row.goodsId"></el-input>
                      </div>
                      <div class="aisles-editor__field" v-if="showAge">
                        <label class="aisles-editor__label">{{ $t("userMachineLineProduct.age") }}</label>
                        <el-input v-model.lazy="row.ageNum" size="small" :disabled="!row.goodsId"></el-input>
                      </div>
                    </div>
                    <div class="aisles-editor__row aisles-editor__row--three">
                      <div class="aisles-editor__field">
                        <label class="aisles-editor__label">{{ $t("userMachineLineProduct.maxAllowedStock") }}</label>
                        <el-input v-model.lazy="row.maxAllowedStock" size="small"></el-input>
                      </div>
                      <div class="aisles-editor__field">
                        <label class="aisles-editor__label">{{ $t("userMachineLineProduct.currentInventoryLevel") }}</label>
                        <el-input v-model.lazy="row.currentInventoryLevel" size="small"></el-input>
                      </div>
                      <div class="aisles-editor__field">
                        <label class="aisles-editor__label">{{ $t("userMachineLineProduct.inventoryAlertCount") }}</label>
                        <el-input v-model.lazy="row.inventoryAlertCount" size="small"></el-input>
                      </div>
                    </div>
                    <div class="aisles-editor__field aisles-editor__switch">
                      <label class="aisles-editor__label">{{ $t("aisles.enabled") }}</label>
                      <el-switch
                        v-model="row.faultStatus"
                        :active-value="0"
                        :inactive-value="1"
                        active-color="var(--vtm-ok)"
                        inactive-color="var(--vtm-crit)"
                      ></el-switch>
                    </div>
                    <!-- 单个货道显式保存：不用再回到抽屉底部的整体保存 -->
                    <div class="aisles-editor__foot">
                      <el-button size="small" :disabled="!isDirty(row) || rowSaving" @click="revertRow(row)">{{ $t("common.cancel") }}</el-button>
                      <el-button type="primary" size="small" :disabled="!isDirty(row)" :loading="rowSaving" @click="saveRow(row)">{{
                        $t("common.save")
                      }}</el-button>
                    </div>
                  </div>

                  <div
                    slot="reference"
                    class="aisles-card"
                    :class="[
                      'aisles-card--' + rowStatus(row),
                      { 'aisles-card--dirty': isDirty(row), 'aisles-card--off': isDisabledRow(row) }
                    ]"
                  >
                    <div class="aisles-card__top">
                      <span class="aisles-card__no vtm-mono">{{ row.aisleNo }}</span>
                      <!-- 与移动端一致：停用货道用红色边框+禁用图标表达，
                           而不是一枚写着"启用状态"的标签（看不出到底是启用还是停用） -->
                      <i
                        v-if="isDisabledRow(row)"
                        class="el-icon-remove-outline aisles-card__off-icon"
                        :title="$t('aisles.chipDisabled')"
                      ></i>
                    </div>
                    <div class="aisles-card__img">
                      <el-image v-if="row.url" :src="row.url" fit="scale-down">
                        <div slot="error" class="aisles-card__img-fallback"><i class="el-icon-picture-outline"></i></div>
                      </el-image>
                      <div v-else class="aisles-card__img-fallback"><i class="el-icon-goods"></i></div>
                    </div>
                    <div class="aisles-card__name">{{ productName(row) }}</div>
                    <div class="aisles-card__stock">
                      <i class="aisles-card__status-icon" :class="statusIcon(rowStatus(row))"></i>
                      <button
                        type="button"
                        class="aisles-card__step"
                        :disabled="!row.goodsId"
                        @click.stop="stepStock(row, -1)"
                      >−</button>
                      <input
                        type="number"
                        class="aisles-card__count vtm-num"
                        min="0"
                        :max="toNum(row.maxAllowedStock)"
                        :disabled="!row.goodsId"
                        v-model.number="row.currentInventoryLevel"
                        @click.stop
                        @change="clampStock(row)"
                      />
                      <span class="aisles-card__slash vtm-num">/ {{ toNum(row.maxAllowedStock) }}</span>
                      <button
                        type="button"
                        class="aisles-card__step"
                        :disabled="!row.goodsId"
                        @click.stop="stepStock(row, 1)"
                      >+</button>
                    </div>
                    <div class="aisles-card__bar">
                      <div
                        class="aisles-card__bar-fill"
                        :class="'aisles-card__bar-fill--' + rowStatus(row)"
                        :style="{ width: fillPct(row) + '%' }"
                      ></div>
                    </div>
                    <el-button
                      v-if="row.goodsId && !isDisabledRow(row)"
                      size="mini"
                      class="aisles-card__fill"
                      icon="el-icon-top"
                      @click.stop="fillOne(row)"
                    >{{ $t("aisles.fill") }}</el-button>
                  </div>
                </el-popover>
            </div>
          </div>

          <div v-else class="aisles__table-wrap">
            <!-- 表格占满面板高度、内部滚动：列宽合计约 1340px，大于面板，横向滚动条要始终在可视区底部 -->
            <el-table ref="table" :data="filteredRows" :empty-text="$t('common.noData')" class="aisles-table" height="100%">
              <el-table-column :label="$t('userMachineLineProduct.aisleNum')" width="80" align="center">
                <template v-slot="scope"><span class="vtm-mono">{{ scope.row.aisleNo }}</span></template>
              </el-table-column>
              <el-table-column :label="$t('userMachineLineProduct.goodsId')" width="230" align="center">
                <template v-slot="scope">
                  <el-select
                    v-model="scope.row.goodsId"
                    style="width: 100%"
                    clearable
                    filterable
                    remote
                    :no-data-text="$t('common.noData')"
                    :no-match-text="$t('common.noData')"
                    :remote-method="handleFilter"
                    :placeholder="$t('userMachineLineProduct.select')"
                    v-el-select-loadmore="loadMore"
                    v-remove-readonly
                    @focus="onSelectFocus(scope.row)"
                    @visible-change="onSelectVisible"
                    @clear="clearRow(scope.row)"
                    @change="productChange(scope.row)"
                  >
                    <el-option v-for="item in allGoodsList" :key="item.id" :label="item.name" :value="item.id"></el-option>
                    <el-option v-if="goodsLoading" disabled class="loading-option">
                      <span class="loading-text"><i class="el-icon-loading"></i> loading...</span>
                    </el-option>
                  </el-select>
                </template>
              </el-table-column>
              <el-table-column :label="$t('userMachineLineProduct.image')" width="80" align="center">
                <template v-slot="scope">
                  <el-image style="width: 36px; height: 36px; display: block; margin: auto" :src="scope.row.url" fit="scale-down">
                    <div slot="error" class="aisles-table__img-fallback"><i class="el-icon-picture-outline"></i></div>
                  </el-image>
                </template>
              </el-table-column>
              <el-table-column :label="$t('userMachineLineProduct.productPrice')" width="90" align="center">
                <template v-slot="scope"><span class="vtm-num">{{ scope.row.costPrice }}</span></template>
              </el-table-column>
              <el-table-column :label="$t('userMachineLineProduct.sellingPrice')" width="110" align="center">
                <template v-slot="scope">
                  <el-input
                    v-model.lazy="scope.row.sellingPrice"
                    size="small"
                    :disabled="!scope.row.goodsId"
                    @blur="checkSellingPrice(scope.row)"
                  ></el-input>
                </template>
              </el-table-column>
              <el-table-column v-if="showVat" :label="$t('userMachineLineProduct.vatRateSet')" width="100" align="center">
                <template v-slot="scope">
                  <el-input v-model.lazy="scope.row.vatRate" size="small" type="number" :disabled="!scope.row.goodsId"></el-input>
                </template>
              </el-table-column>
              <el-table-column v-if="showAge" :label="$t('userMachineLineProduct.age')" width="90" align="center">
                <template v-slot="scope">
                  <el-input v-model.lazy="scope.row.ageNum" size="small" :disabled="!scope.row.goodsId"></el-input>
                </template>
              </el-table-column>
              <el-table-column :label="$t('userMachineLineProduct.maxAllowedStock')" width="100" align="center">
                <template v-slot="scope"><el-input v-model.lazy="scope.row.maxAllowedStock" size="small"></el-input></template>
              </el-table-column>
              <el-table-column :label="$t('userMachineLineProduct.currentInventoryLevel')" width="120" align="center">
                <template v-slot="scope">
                  <el-input
                    v-model.lazy="scope.row.currentInventoryLevel"
                    size="small"
                    :disabled="!scope.row.goodsId"
                    :class="'aisles-input--' + rowStatus(scope.row)"
                    @change="clampStock(scope.row)"
                  >
                    <i slot="prefix" :class="statusIcon(rowStatus(scope.row))"></i>
                  </el-input>
                </template>
              </el-table-column>
              <el-table-column :label="$t('userMachineLineProduct.inventoryAlertCount')" width="100" align="center">
                <template v-slot="scope"><el-input v-model.lazy="scope.row.inventoryAlertCount" size="small"></el-input></template>
              </el-table-column>
              <el-table-column :label="$t('userMachineLineProduct.isFault2')" width="90" align="center">
                <template v-slot="scope">
                  <el-switch
                    v-model="scope.row.faultStatus"
                    :active-value="0"
                    :inactive-value="1"
                    active-color="var(--vtm-ok)"
                    inactive-color="var(--vtm-crit)"
                  ></el-switch>
                </template>
              </el-table-column>
              <el-table-column :label="$t('common.operate')" width="150" align="center" fixed="right">
                <template v-slot="scope">
                  <el-button size="mini" plain round :disabled="!scope.row.goodsId" @click="copy(scope.row)">{{
                    $t("userMachineLineProduct.copy")
                  }}</el-button>
                  <el-button size="mini" plain round @click="paste(scope.row)">{{
                    $t("userMachineLineProduct.paste")
                  }}</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </template>
      </div>

      <div class="aisles__footer">
        <div class="aisles__footer-left">
          <span v-if="dirtyCount" class="aisles__dirty">{{ $t("aisles.pendingChanges", { n: dirtyCount }) }}</span>
          <el-button v-if="dirtyCount" type="text" size="small" @click="resetChanges">{{ $t("aisles.reset") }}</el-button>
        </div>
        <div class="aisles__footer-right">
          <el-button size="small" @click="requestClose">{{ $t("common.cancel") }}</el-button>
          <el-button type="primary" size="small" :disabled="dirtyCount === 0" :loading="saving" @click="submitForm">{{
            $t("common.definite")
          }}</el-button>
        </div>
      </div>
    </div>
    <!-- 商品库为空时就地新建商品，避免离开设备货道页去商品页再回来 -->
    <add-product
      :dialogProduct="addProductOpen"
      :product="newProduct"
      :productTags="[]"
      v-on:cancel="onAddProductClose"
    />
  </el-drawer>
</template>

<script>
  import addProduct from "_c/product/AddProduct";

  const AISLE_VIEW_KEY = "vtm-aisle-editor-view";

  export default {
    name: "UserMachineLineProduct",
    components: { addProduct },
    props: {
      dialogMachineLineProduct: Boolean,
      userEquipment: Object
    },
    beforeDestroy() {
      clearTimeout(this.goodsRetryTimer);
    },
    data() {
      return {
        loading: false,
        saving: false,
        addProductOpen: false,
        newProduct: {},
        exportLoading: false,
        model: {
          lineProductList: [],
          page: {
            pageSize: 500,
            pageIndex: 1,
            totalSize: 0
          }
        },
        mode: 1,
        view: "restock",
        search: "",
        activeChip: "all",
        copyData: null,
        activeRow: null,
        baselineMap: {},
        trackedFields: [
          "goodsId",
          "url",
          "costPrice",
          "sellingPrice",
          "vatRate",
          "ageNum",
          "maxAllowedStock",
          "currentInventoryLevel",
          "inventoryAlertCount",
          "faultStatus"
        ],
        baseGoodsList: [], // 需要回显的基础商品列表
        goodsList: [],
        goodsLoading: false,
        rowSaving: false, // 单格保存中
        goodsReqId: 0, // 商品下拉请求序号，只采用最新一次的结果
        goodsKey: "", // 正在请求的 页码|关键字
        goodsRetryTimer: null,
        goodsLock: true,
        pageNum: 1,
        pageSize: 20,
        searchGoodsName: "",
        debounceTimer: null
      };
    },
    computed: {
      show: {
        get() {
          return this.dialogMachineLineProduct;
        },
        set(value) {
          if (value) {
            return;
          }
          this.requestClose();
        }
      },
      rows() {
        return this.model.lineProductList;
      },
      showVat() {
        const config = this.userEquipment && this.userEquipment.config;
        return !!(config && config.vatRate && config.vatRate.isOpen == 1 && config.vatRate.type == 2);
      },
      showAge() {
        const config = this.userEquipment && this.userEquipment.config;
        return !!(config && config.age && config.age.ageRange == 1);
      },
      chips() {
        return [
          { key: "all", label: this.$t("aisles.chipAll"), count: this.rows.length },
          { key: "low", label: this.$t("aisles.chipLow"), count: this.rows.filter(this.isLowRow).length },
          { key: "empty", label: this.$t("aisles.chipEmpty"), count: this.rows.filter(this.isEmptyRow).length },
          { key: "unassigned", label: this.$t("aisles.chipUnassigned"), count: this.rows.filter(this.isUnassignedRow).length },
          { key: "disabled", label: this.$t("aisles.chipDisabled"), count: this.rows.filter(this.isDisabledRow).length }
        ];
      },
      filteredRows() {
        let list = this.rows;
        if (this.activeChip === "low") {
          list = list.filter(this.isLowRow);
        } else if (this.activeChip === "empty") {
          list = list.filter(this.isEmptyRow);
        } else if (this.activeChip === "unassigned") {
          list = list.filter(this.isUnassignedRow);
        } else if (this.activeChip === "disabled") {
          list = list.filter(this.isDisabledRow);
        }
        const q = (this.search || "").trim().toLowerCase();
        if (q) {
          list = list.filter(row => {
            const name = this.productName(row).toLowerCase();
            return String(row.aisleNo).toLowerCase().indexOf(q) > -1 || name.indexOf(q) > -1;
          });
        }
        return list;
      },
      // 按货道号排序的平铺列表（螺旋货道机无货架概念）
      sortedRows() {
        return this.filteredRows.slice().sort((a, b) => (parseInt(a.aisleNo, 10) || 0) - (parseInt(b.aisleNo, 10) || 0));
      },
      dirtyRows() {
        return this.rows.filter(this.isDirty);
      },
      dirtyCount() {
        return this.dirtyRows.length;
      },
      pickListItems() {
        const map = {};
        const order = [];
        this.rows.forEach(row => {
          if (!row.goodsId || this.isDisabledRow(row)) {
            return;
          }
          const max = this.toNum(row.maxAllowedStock);
          const current = this.toNum(row.currentInventoryLevel);
          const need = max - current;
          if (need <= 0) {
            return;
          }
          const key = row.goodsId;
          if (!map[key]) {
            map[key] = { name: this.productName(row), units: 0, slots: [] };
            order.push(key);
          }
          map[key].units += need;
          map[key].slots.push({ aisleNo: row.aisleNo, need });
        });
        return order.map(key => map[key]).sort((a, b) => b.units - a.units);
      },
      allGoodsList() {
        if (!this.baseGoodsList || !this.goodsList) {
          return [];
        }
        const selectedGoodsId = this.activeRow ? this.activeRow.goodsId : null;
        if (this.searchGoodsName) {
          const results = [...this.goodsList];
          const selectedGoodsInBase = this.baseGoodsList.find(item => item.id === selectedGoodsId);
          if (selectedGoodsInBase && !results.some(item => item.id === selectedGoodsId)) {
            results.unshift(selectedGoodsInBase);
          }
          return results;
        }
        const mergedList = [...this.baseGoodsList];
        const filteredGoodsList = this.goodsList.filter(
          goodsItem => !this.baseGoodsList.some(baseItem => baseItem.id === goodsItem.id)
        );
        return mergedList.concat(filteredGoodsList);
      }
    },
    methods: {
      // ---- 打开 / 关闭 ----
      openDrawer() {
        // 记住客户上次用的视图（表格/卡片），不再每次打开都切回卡片
        this.view = localStorage.getItem(AISLE_VIEW_KEY) === "table" ? "table" : "restock";
        this.search = "";
        this.activeChip = "all";
        this.copyData = null;
        this.mode = this.userEquipment.lineProductMode ? +this.userEquipment.lineProductMode + 1 : 1;
        this.loadRows();
        // 预先加载第一页商品：远程下拉在没有选项时根本不会展开，空货道就选不到商品
        this.resetProductSearch();
      },
      rememberView(v) {
        try {
          localStorage.setItem(AISLE_VIEW_KEY, v);
        } catch (e) {
          /* 隐私模式等场景忽略 */
        }
      },
      onClosed() {
        this.copyData = null;
        this.activeRow = null;
        this.search = "";
        this.activeChip = "all";
        this.resetGoods();
        this.baseGoodsList = [];
        this.searchGoodsName = "";
        this.pageNum = 1;
        this.model.lineProductList = [];
        this.baselineMap = {};
      },
      cancel(state, code) {
        this.$emit("lineProductCancel", false, state, code);
      },
      requestClose() {
        if (this.dirtyCount > 0) {
          this.$confirm(this.$t("aisles.discardConfirm"), this.$t("common.tip"), {
            confirmButtonText: this.$t("common.definite"),
            cancelButtonText: this.$t("common.cancel"),
            type: "warning"
          })
            .then(() => {
              this.cancel("");
            })
            .catch(() => {});
        } else {
          this.cancel("");
        }
      },

      // ---- 加载货道商品 ----
      loadRows() {
        this.loading = true;
        this.model.lineProductList = [];
        this.fetchRowsPage([], 1);
      },
      // 逐页拉取并累积全部货道行（避免 >500 行的机器丢失后续货道）
      fetchRowsPage(acc, pageIndex) {
        const requestData = {
          equipmentId: this.userEquipment.id,
          pageIndex,
          pageSize: this.model.page.pageSize
        };
        this.$api
          .request(this.$url.UserMachine.listProduct, this.$method.get, requestData)
          .then(res => {
            if (res.code === 0 && res.data) {
              const records = res.data.records || [];
              const all = acc.concat(records);
              const total = parseInt(res.data.total, 10) || all.length;
              // 仍有剩余且本页非空则续拉；pageIndex 上限为安全阀，防止后端 total 异常导致死循环
              if (records.length > 0 && all.length < total && pageIndex < 50) {
                this.fetchRowsPage(all, pageIndex + 1);
                return;
              }
              this.processRows(all, total);
            }
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
          });
      },
      processRows(records, total) {
        const baseGoodsArr = [];
        const baseline = {};
        records.forEach(item => {
          // 先按「原始值」记录基线（含未修正的 goodsId），再做自愈修正，
          // 这样 goodsId 不一致的行会被判为脏行并在保存时提交，真正修复后端数据。
          const snap = { goods: item.goods };
          this.trackedFields.forEach(field => {
            snap[field] = item[field];
          });
          baseline[this.rowKey(item)] = snap;

          if (item.goodsId && item.goods) {
            if (!baseGoodsArr.some(g => g.id === item.goods.id)) {
              baseGoodsArr.push(item.goods);
            }
            if (item.goodsId !== item.goods.id) {
              item.goodsId = item.goods.id;
            }
          } else if (item.goodsId && !item.goods) {
            if (!baseGoodsArr.some(g => g.id === item.goodsId)) {
              baseGoodsArr.push({
                id: item.goodsId,
                name: `Product ${String(item.goodsId).substring(0, 8)}...`,
                costPrice: item.costPrice || "0",
                url: item.url || ""
              });
            }
          }
        });
        this.baseGoodsList = baseGoodsArr;
        this.model.lineProductList = records;
        this.model.page.totalSize = total;
        this.baselineMap = baseline;
        this.loading = false;
      },
      // 行标识：以 id 为主，缺失时回退到货道号（同机内唯一）
      rowKey(row) {
        return row.id || "aisle-" + row.aisleNo;
      },
      captureBaseline() {
        const map = {};
        this.rows.forEach(row => {
          const snapshot = { goods: row.goods };
          this.trackedFields.forEach(field => {
            snapshot[field] = row[field];
          });
          map[this.rowKey(row)] = snapshot;
        });
        this.baselineMap = map;
      },

      // ---- 状态判定 ----
      toNum(value) {
        const n = parseInt(value, 10);
        return isNaN(n) ? 0 : n;
      },
      isLowRow(row) {
        return !!row.goodsId && this.toNum(row.currentInventoryLevel) > 0 && this.toNum(row.currentInventoryLevel) <= this.toNum(row.inventoryAlertCount);
      },
      isEmptyRow(row) {
        return !!row.goodsId && this.toNum(row.currentInventoryLevel) === 0;
      },
      isUnassignedRow(row) {
        return !row.goodsId;
      },
      isDisabledRow(row) {
        return Number(row.faultStatus) === 1;
      },
      rowStatus(row) {
        if (this.isDisabledRow(row)) {
          return "disabled";
        }
        if (this.isUnassignedRow(row)) {
          return "unassigned";
        }
        if (this.isEmptyRow(row)) {
          return "empty";
        }
        if (this.isLowRow(row)) {
          return "low";
        }
        return "ok";
      },
      statusIcon(status) {
        const icons = {
          ok: "el-icon-check",
          low: "el-icon-warning-outline",
          empty: "el-icon-remove-outline",
          unassigned: "el-icon-minus",
          disabled: "el-icon-lock"
        };
        return icons[status] || "el-icon-minus";
      },
      isDirty(row) {
        const base = this.baselineMap[this.rowKey(row)];
        if (!base) {
          return false;
        }
        return this.trackedFields.some(field => {
          const current = row[field] === undefined || row[field] === null ? "" : String(row[field]);
          const original = base[field] === undefined || base[field] === null ? "" : String(base[field]);
          return current !== original;
        });
      },
      productName(row) {
        if (row.goods && row.goods.name) {
          return row.goods.name;
        }
        if (row.goodsId) {
          const found = this.baseGoodsList.find(item => item.id === row.goodsId);
          if (found) {
            return found.name;
          }
        }
        return this.$t("aisles.unassigned");
      },
      fillPct(row) {
        const max = this.toNum(row.maxAllowedStock);
        if (!max) {
          return 0;
        }
        const pct = (this.toNum(row.currentInventoryLevel) / max) * 100;
        if (pct < 0) {
          return 0;
        }
        if (pct > 100) {
          return 100;
        }
        return pct;
      },
      slotsLabel(item) {
        return item.slots.map(slot => `${slot.aisleNo}×${slot.need}`).join(", ");
      },

      // ---- 过滤 chip ----
      toggleChip(key) {
        this.activeChip = this.activeChip === key ? "all" : key;
        if (key === "all") {
          this.activeChip = "all";
        }
      },

      // ---- 库存快捷操作 ----
      stepStock(row, delta) {
        if (!row.goodsId) {
          return;
        }
        const max = this.toNum(row.maxAllowedStock);
        let value = this.toNum(row.currentInventoryLevel) + delta;
        if (value < 0) {
          value = 0;
        }
        if (value > max) {
          value = max;
        }
        this.$set(row, "currentInventoryLevel", value);
      },
      clampStock(row) {
        const max = this.toNum(row.maxAllowedStock);
        let value = this.toNum(row.currentInventoryLevel);
        if (value < 0) {
          value = 0;
        }
        if (value > max) {
          value = max;
        }
        this.$set(row, "currentInventoryLevel", value);
      },
      fillOne(row) {
        this.$set(row, "currentInventoryLevel", row.maxAllowedStock);
      },

      // ---- 批量操作（作用于当前筛选出的可见货道）----
      fillAll() {
        let n = 0;
        this.filteredRows.forEach(row => {
          if (row.goodsId && !this.isDisabledRow(row)) {
            this.$set(row, "currentInventoryLevel", row.maxAllowedStock);
            n++;
          }
        });
        if (n) {
          this.$message.success(this.$t("aisles.filledN", { n }));
        }
      },
      fillLow() {
        let n = 0;
        this.filteredRows.forEach(row => {
          if (row.goodsId && !this.isDisabledRow(row) && this.toNum(row.currentInventoryLevel) <= this.toNum(row.inventoryAlertCount)) {
            this.$set(row, "currentInventoryLevel", row.maxAllowedStock);
            n++;
          }
        });
        if (n) {
          this.$message.success(this.$t("aisles.filledN", { n }));
        }
      },
      allPaste() {
        if (!this.copyData || !this.copyData.goodsId) {
          this.$message.warning(this.$t("userMachineLineProduct.one"));
          return;
        }
        this.filteredRows.forEach(row => {
          Object.keys(this.copyData).forEach(key => {
            if (key !== "id" && key !== "aisleNo" && key !== "mergeLineNum") {
              this.$set(row, key, this.copyData[key]);
            }
          });
        });
      },

      // ---- 拿货清单 ----
      copyPickList() {
        if (!this.pickListItems.length) {
          return;
        }
        const text = this.pickListItems
          .map(item => `${item.name} — ${item.units} (${this.slotsLabel(item)})`)
          .join("\n");
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard
            .writeText(text)
            .then(() => {
              this.$message.success(this.$t("aisles.pickListCopied"));
            })
            .catch(() => {});
        }
      },

      // ---- 单货道复制 / 粘贴 ----
      copy(row) {
        if (row.goodsId) {
          this.copyData = { ...row };
          this.$message.success(this.$t("aisles.copyDone"));
        } else {
          this.copyData = null;
          this.$message.warning(this.$t("userMachineLineProduct.lineInfo"));
        }
      },
      paste(row) {
        if (!this.copyData || !this.copyData.goodsId) {
          this.$message.warning(this.$t("aisles.pasteFirst"));
          return;
        }
        Object.keys(this.copyData).forEach(key => {
          if (key !== "id" && key !== "aisleNo" && key !== "mergeLineNum") {
            this.$set(row, key, this.copyData[key]);
          }
        });
      },

      // ---- 商品选择 ----
      onSelectFocus(row) {
        this.activeRow = row;
        // 上一个货道的搜索结果不要带到这个货道；列表为空时补加载
        // 第一页已在加载（刚打开面板）就等它回来，不要重置后重发
        if (this.searchGoodsName || (!this.goodsList.length && !this.goodsLoading)) {
          this.resetProductSearch();
        }
      },
      // 下拉关闭时清掉搜索词：否则选项只剩搜索结果，其它货道的下拉找不到自己的商品，
      // 会直接显示商品 ID（要刷新页面才恢复）
      onSelectVisible(open) {
        if (!open && this.searchGoodsName) {
          this.resetProductSearch();
        }
      },
      resetProductSearch() {
        this.searchGoodsName = "";
        this.pageNum = 1;
        this.resetGoods();
        this.goodsLock = true;
        this.getProducts();
      },
      clearRow(row) {
        this.$set(row, "goodsId", "");
        this.$set(row, "goods", null);
        this.$set(row, "url", "");
        this.$set(row, "costPrice", "0");
        this.$set(row, "sellingPrice", "0");
        this.$set(row, "ageNum", "0");
        this.$set(row, "vatRate", "0");
        this.$set(row, "maxAllowedStock", "0");
        this.$set(row, "currentInventoryLevel", "0");
        this.$set(row, "inventoryAlertCount", "0");
      },
      productChange(row) {
        const id = row.goodsId;
        if (!id) {
          this.clearRow(row);
          return;
        }
        const product = this.allGoodsList.find(item => item.id === id);
        if (product) {
          this.$set(row, "costPrice", product.costPrice);
          this.$set(row, "url", product.url);
          this.$set(row, "goods", product);
          // 选中的商品（可能来自搜索结果）加入回显列表，搜索清空后下拉仍能显示商品名而不是 ID
          if (!this.baseGoodsList.some(g => g.id === product.id)) {
            this.baseGoodsList.push(product);
          }
          // 换了商品：本机其它货道已有同款时沿用它的零售价（原价格属于被换掉的商品，不能留着）
          // 换回这一格原来的商品就恢复原价；都没有就清空，让用户填新商品的价格（保存时会校验必填）
          const same = this.rows.find(r => r !== row && r.goodsId === id && Number(r.sellingPrice));
          const base = this.baselineMap[this.rowKey(row)];
          if (base && base.goodsId === id && Number(base.sellingPrice)) {
            this.$set(row, "sellingPrice", base.sellingPrice);
          } else if (same) {
            this.$set(row, "sellingPrice", same.sellingPrice);
          } else if (base && base.goodsId && base.goodsId !== id && String(row.sellingPrice) === String(base.sellingPrice)) {
            // 只有价格还是被换掉商品的原价时才清空；空货道或用户已经填过的价格保留
            this.$set(row, "sellingPrice", "");
          }
        }
      },
      checkSellingPrice(row) {
        if (row.sellingPrice && row.costPrice) {
          const sellingPrice = parseFloat(row.sellingPrice);
          const costPrice = parseFloat(row.costPrice);
          if (!isNaN(sellingPrice) && !isNaN(costPrice) && sellingPrice < costPrice) {
            this.$confirm(this.$t("aisles.belowCost"), this.$t("common.tip"), {
              confirmButtonText: this.$t("common.definite"),
              cancelButtonText: this.$t("common.cancel"),
              type: "warning"
            })
              .then(() => {})
              .catch(() => {
                this.$set(row, "sellingPrice", row.costPrice);
              });
          }
        }
      },
      // 商品库为空时就地新建商品
      openAddProduct() {
        this.newProduct = {};
        this.addProductOpen = true;
      },
      // AddProduct 关闭：保存成功(state 为真)时刷新商品列表，让新商品立刻可选
      onAddProductClose(visible, state) {
        this.addProductOpen = false;
        if (state) {
          this.searchGoodsName = "";
          this.pageNum = 1;
          this.resetGoods();
          this.getProducts();
        }
      },
      loadMore() {
        if (!this.goodsLoading) {
          this.getProducts();
        }
      },
      handleFilter(query) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          this.searchGoodsName = query;
          this.pageNum = 1;
          this.resetGoods();
          this.getProducts();
        }, 300);
      },
      // 清空商品下拉列表：作废还没回来的旧请求、取消排队的补发
      resetGoods() {
        this.goodsList = [];
        this.goodsReqId++;
        this.goodsLoading = false;
        clearTimeout(this.goodsRetryTimer);
      },
      getProducts() {
        // 同一页同一关键字已在请求中就不再发（打开面板和点下拉会各发一次第一页，结果会重复）
        const key = this.pageNum + "|" + (this.searchGoodsName || "");
        if (this.goodsLoading && this.goodsKey === key) {
          return;
        }
        // 300ms 节流内被挡下时稍后补发，而不是直接丢掉（否则关键字改了却没有新请求）
        if (!this.goodsLock) {
          clearTimeout(this.goodsRetryTimer);
          this.goodsRetryTimer = setTimeout(() => this.getProducts(), 320);
          return;
        }
        this.goodsKey = key;
        // 只认最后一次请求的结果：关键字变了或列表被重置后，旧请求晚回来要丢掉
        const reqId = ++this.goodsReqId;
        this.goodsLock = false;
        setTimeout(() => (this.goodsLock = true), 300);
        this.goodsLoading = true;
        this.$api
          .request(this.$url.Product.page, this.$method.get, {
            pageIndex: this.pageNum,
            pageSize: this.pageSize,
            name: this.searchGoodsName,
            uid: this.userEquipment.uid
          })
          .then(res => {
            if (reqId !== this.goodsReqId) {
              return;
            }
            if (res.code === 0) {
              const { records } = res.data;
              if (records.length) {
                this.pageNum++;
                this.goodsList = [...this.goodsList, ...records];
              }
            }
          })
          .finally(() => {
            if (reqId === this.goodsReqId) {
              this.goodsLoading = false;
            }
          });
      },

      // ---- 重置 / 保存 ----
      resetChanges() {
        this.rows.forEach(row => {
          const base = this.baselineMap[this.rowKey(row)];
          if (!base) {
            return;
          }
          this.trackedFields.forEach(field => {
            this.$set(row, field, base[field]);
          });
          this.$set(row, "goods", base.goods);
        });
      },
      scrollToRow(row) {
        // 清除筛选，确保表格行序号与 DOM 行一一对应
        this.view = "table";
        this.activeChip = "all";
        this.search = "";
        this.$nextTick(() => {
          const idx = this.filteredRows.findIndex(r => this.rowKey(r) === this.rowKey(row));
          if (idx === -1 || !this.$refs.table) {
            return;
          }
          const nodes = this.$refs.table.$el.querySelectorAll(".el-table__body-wrapper .el-table__row");
          const el = nodes[idx];
          if (el && el.scrollIntoView) {
            el.scrollIntoView({ block: "center" });
          }
        });
      },
      // 仅校验将被提交的行（脏行）。旧实现遍历全部行，导致某个未改动的历史行
      // （如库存超 3 位）阻塞与其无关的编辑保存。
      validateAll(rows, scroll = true) {
        const sellReg = /(^[1-9]([0-9]+)?(\.[0-9]{1,2})?$)|(^(0){1}$)|(^[0-9]\.[0-9]([0-9])?$)/;
        const posReg = /^[1-9][0-9]{0,2}$/;
        const targets = rows || this.dirtyRows;
        for (let i = 0; i < targets.length; i++) {
          const row = targets[i];
          if (!row.goodsId) {
            continue;
          }
          const prefix = this.$t("userMachineLineProduct.line").replace("N", row.aisleNo);
          let text = "";
          if (!row.sellingPrice || !sellReg.test(String(row.sellingPrice))) {
            text += this.$t("userMachineLineProduct.decimal");
          }
          if (parseInt(row.maxAllowedStock, 10) <= 0) {
            text += this.$t("userMachineLineProduct.vMaxStock");
          } else if (!posReg.test(String(row.maxAllowedStock))) {
            text += this.$t("userMachineLineProduct.vMaxStockN");
          }
          if (parseInt(row.currentInventoryLevel, 10) < 0) {
            text += this.$t("userMachineLineProduct.vNowStock");
          } else if (parseInt(row.currentInventoryLevel, 10) !== 0 && !posReg.test(String(row.currentInventoryLevel))) {
            text += this.$t("userMachineLineProduct.vNowStockN");
          }
          if (parseInt(row.currentInventoryLevel, 10) > parseInt(row.maxAllowedStock, 10)) {
            text += this.$t("userMachineLineProduct.vNowMax");
          }
          // 0 = 关闭低库存提醒（与 isLowRow 语义一致），允许保存；仅拒绝负数与非法格式
          if (parseInt(row.inventoryAlertCount, 10) < 0) {
            text += this.$t("userMachineLineProduct.vStockAlarm");
          } else if (parseInt(row.inventoryAlertCount, 10) !== 0 && !posReg.test(String(row.inventoryAlertCount))) {
            text += this.$t("userMachineLineProduct.vStockAlarmN");
          }
          if (text) {
            this.$message.warning(prefix + text);
            if (scroll) this.scrollToRow(row);
            return false;
          }
        }
        return true;
      },
      buildPayload(rows) {
        return rows.map(row => {
          const clone = { ...row };
          if (clone.ageNum) {
            clone.ageNum = parseInt(clone.ageNum, 10);
          }
          // 与旧实现保持一致：age 字段提交前转整型
          if (clone.age) {
            clone.age = parseInt(clone.age, 10);
          }
          return clone;
        });
      },
      closeRowPopover(row) {
        const ref = this.$refs["pop_" + this.rowKey(row)];
        const pop = Array.isArray(ref) ? ref[0] : ref;
        if (pop && pop.doClose) pop.doClose();
      },
      // 单个货道：撤销这一格的改动
      revertRow(row) {
        const base = this.baselineMap[this.rowKey(row)];
        if (base) {
          this.trackedFields.forEach(field => {
            this.$set(row, field, base[field]);
          });
          this.$set(row, "goods", base.goods);
        }
        this.closeRowPopover(row);
      },
      // 连续保存两个不同货道依赖后端 @RepeatSubmit 按请求内容判重（RepeatSubmitAspect），这里直接发
      sendUpdate(payload) {
        return this.$api.request(this.$url.MachineLineProduct.update, this.$method.put, payload);
      },
      // 单个货道：只保存这一格
      saveRow(row) {
        // 整体保存进行中时不单独保存（两个请求会包含同一格，后返回的会覆盖已保存基线）
        if (this.rowSaving || this.saving || !this.isDirty(row) || !this.validateAll([row], false)) {
          return;
        }
        this.rowSaving = true;
        this.sendUpdate(this.buildPayload([row]))
          .then(res => {
            this.rowSaving = false;
            if (res.code === 0) {
              // 只把这一格记为已保存，其它格的未保存改动保持不变
              const snap = { goods: row.goods };
              this.trackedFields.forEach(field => {
                snap[field] = row[field];
              });
              this.$set(this.baselineMap, this.rowKey(row), snap);
              this.$message.success(this.$t("aisles.slotSaved", { n: row.aisleNo }));
              this.closeRowPopover(row);
            } else {
              this.$notify({
                title: this.$t("common.tip"),
                message: res.msg || "Save failed. Please try again.",
                type: "warning",
                duration: 4500
              });
            }
          })
          .catch(() => {
            this.rowSaving = false;
          });
      },
      submitForm() {
        // 立即锁定按钮，防止在确认弹窗展示期间重复点击触发两次保存
        // （两次请求间隔小于后端 @RepeatSubmit 的 3 秒窗口时，第二次会被静默拒绝）
        if (this.saving || this.rowSaving) {
          return;
        }
        this.saving = true;
        this.$confirm(this.$t("userMachineLineProduct.confirmSaveMsg"), this.$t("common.tip"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        })
          .then(() => {
            this.doSubmit();
          })
          .catch(() => {
            this.saving = false;
          });
      },
      doSubmit() {
        if (!this.validateAll()) {
          this.saving = false;
          return;
        }
        const dirty = this.dirtyRows;
        if (!dirty.length) {
          this.saving = false;
          return;
        }
        this.saving = true;
        const submitData = this.buildPayload(dirty);
        this.sendUpdate(submitData)
          .then(res => {
            this.saving = false;
            if (res.code === 0) {
              this.$notify({
                title: this.$t("common.tip"),
                message: res.msg,
                type: "success"
              });
              this.$emit("changeOpenMode", this.mode);
              this.captureBaseline();
              this.cancel("");
            } else {
              // 保存未成功时明确提示（例如后端 3 秒内的重复提交拦截），
              // 而不是让弹窗停在原地、没有任何反馈
              this.$notify({
                title: this.$t("common.tip"),
                message: res.msg || "Save failed. Please try again.",
                type: "warning",
                duration: 4500
              });
            }
          })
          .catch(() => {
            this.saving = false;
          });
      },

      // ---- 导出 ----
      handlerExportLine() {
        this.$confirm(
          "Are you sure you want to export all cargo lane information in this device?",
          this.$t("common.tip"),
          {
            confirmButtonText: this.$t("common.definite"),
            cancelButtonText: this.$t("common.cancel"),
            type: "warning"
          }
        )
          .then(() => {
            this.exportLoading = true;
            this.$api
              .download(this.$url.MachineLineProduct.export, {
                equipmentId: this.userEquipment.id
              })
              .then(res => {
                this.exportLoading = false;
                if (res.code === 0) {
                  this.$message.success("success");
                } else {
                  this.$message.warning(res.msg);
                }
              })
              .catch(() => {
                this.exportLoading = false;
              });
          })
          .catch(err => {
            if (err && err.msg) {
              this.$message.error(err.msg);
            }
          });
      }
    },
    directives: {
      "el-select-loadmore": {
        bind(el, binding) {
          const SELECTWRAP_DOM = el.querySelector(".el-select-dropdown .el-select-dropdown__wrap");
          if (SELECTWRAP_DOM) {
            SELECTWRAP_DOM.addEventListener("scroll", function () {
              const condition = this.scrollHeight - this.scrollTop <= this.clientHeight + 10;
              if (condition) {
                binding.value();
              }
            });
          }
        }
      },
      removeReadonly: {
        inserted(el) {
          const input = el.querySelector(".el-input__inner");
          if (input) {
            const removeReadonly = () => input.removeAttribute("readonly");
            removeReadonly();
            const observer = new MutationObserver(() => {
              removeReadonly();
            });
            observer.observe(input, { attributes: true });
            el._observer = observer;
          }
        },
        unbind(el) {
          if (el._observer) {
            el._observer.disconnect();
            delete el._observer;
          }
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  ::v-deep .vtm-aisles-drawer {
    // 窗口变窄或浏览器/Windows 放大时不能超出屏幕，否则左侧货道号和商品列被裁掉、无法操作
    max-width: 100vw;
    .el-drawer__body {
      padding: 0;
      height: 100%;
      display: flex;
      flex-direction: column;
    }
  }

  .aisles {
    display: flex;
    flex-direction: column;
    height: 100%;
    font-family: var(--vtm-body);
  }

  // ---- 头部 ----
  .aisles__head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 20px 24px 14px;
    border-bottom: 1px solid var(--vtm-line);
  }
  .aisles__head-main {
    flex: 1;
    min-width: 0;
  }
  .aisles__title-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
  }
  .aisles__title {
    font-family: var(--vtm-display);
    font-size: 19px;
    font-weight: 650;
    color: var(--vtm-heading);
  }
  .aisles__machine {
    font-size: 13px;
    color: var(--vtm-ink-2);
  }
  .aisles__no {
    font-size: 12px;
    color: var(--vtm-ink-3);
  }
  .aisles__subtitle {
    margin-top: 4px;
    font-size: 12.5px;
    color: var(--vtm-ink-2);
  }
  .aisles__close {
    flex-shrink: 0;
    border: none;
    background: transparent;
    color: var(--vtm-ink-3);
    font-size: 18px;
    cursor: pointer;

    &:hover {
      color: var(--vtm-crit);
    }
  }

  // ---- KPI 筹码 + 搜索 ----
  .aisles__bar {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 24px 0;
    flex-wrap: wrap;
  }
  .aisles__chips {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    flex: 1;
  }
  .aisles-chip {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: var(--vtm-well);
    border: 1px solid var(--vtm-line);
    border-radius: 999px;
    cursor: pointer;
    transition: background var(--vtm-speed), border-color var(--vtm-speed);

    &:hover {
      border-color: var(--vtm-line-2);
    }
  }
  .aisles-chip__count {
    font-size: 13.5px;
    font-weight: 700;
    color: var(--vtm-ink);
  }
  .aisles-chip__label {
    font-size: 12px;
    color: var(--vtm-ink-2);
    white-space: nowrap;
  }
  .aisles-chip--active {
    background: var(--vtm-mint-soft);
    border-color: var(--vtm-mint);

    .aisles-chip__count,
    .aisles-chip__label {
      color: var(--vtm-mint-ink);
    }
  }
  .aisles__search {
    width: 240px;
    flex-shrink: 0;
  }

  // ---- 工具条 ----
  .aisles__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 24px 0;
    flex-wrap: wrap;
  }
  .aisles__bulk {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .aisles__scope-note {
    padding: 6px 24px 0;
    font-size: 12px;
    color: var(--vtm-ink-3);
  }

  // ---- 内容区（可滚动） ----
  .aisles__content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: auto;
    padding: 16px 24px 20px;
  }
  .aisles__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    height: 100%;
    color: var(--vtm-ink-3);
    font-size: 13.5px;

    i {
      font-size: 34px;
    }
  }

  // ---- 补货网格（平铺，无货架分组）----
  .aisles-shelf__row {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
  }
  .aisles-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px;
    background: var(--vtm-surface);
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-md);
    cursor: pointer;
    transition: border-color var(--vtm-speed), box-shadow var(--vtm-speed);

    &:hover {
      border-color: var(--vtm-line-2);
      box-shadow: var(--vtm-e1);
    }
  }
  .aisles-card--dirty::after {
    content: "";
    position: absolute;
    top: 7px;
    right: 7px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--vtm-mint);
  }
  .aisles-card--disabled,
  .aisles-card--unassigned {
    opacity: 0.62;
  }
  // 停用货道：红边 + 淡红底 + 红色货道号，内容压暗（与移动端一致）
  .aisles-card--off {
    border-color: var(--vtm-crit);
    background: var(--vtm-crit-soft);

    .aisles-card__no {
      color: var(--vtm-crit);
    }
    .aisles-card__img,
    .aisles-card__name {
      opacity: 0.55;
    }
  }
  .aisles-card__off-icon {
    font-size: 15px;
    color: var(--vtm-crit);
  }
  .aisles-card__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .aisles-card__no {
    font-size: 12px;
    font-weight: 700;
    color: var(--vtm-ink-2);
  }
  .aisles-card__img {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 48px;
  }
  .aisles-card__img ::v-deep .el-image {
    width: 48px;
    height: 48px;
  }
  .aisles-card__img-fallback {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--vtm-well);
    border-radius: var(--vtm-r-sm);
    color: var(--vtm-ink-3);
    font-size: 18px;
  }
  .aisles-card__name {
    height: 2.4em;
    font-size: 12px;
    line-height: 1.2;
    color: var(--vtm-ink);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-align: center;
  }
  .aisles-card__stock {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }
  .aisles-card__status-icon {
    font-size: 12px;
    color: var(--vtm-ink-3);
  }
  .aisles-card__step {
    width: 18px;
    height: 18px;
    line-height: 16px;
    padding: 0;
    border: 1px solid var(--vtm-line);
    background: var(--vtm-well);
    border-radius: var(--vtm-r-sm);
    color: var(--vtm-ink-2);
    font-size: 12px;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  .aisles-card__count {
    width: 36px;
    text-align: center;
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-sm);
    font-size: 13px;
    font-weight: 700;
    color: var(--vtm-ink);
    background: var(--vtm-surface);
    padding: 2px 0;
  }
  .aisles-card__slash {
    font-size: 12px;
    color: var(--vtm-ink-3);
  }
  .aisles-card__bar {
    height: 5px;
    border-radius: 4px;
    background: var(--vtm-well);
    overflow: hidden;
  }
  .aisles-card__bar-fill {
    height: 100%;
    border-radius: 4px;

    &--ok { background: var(--vtm-ok); }
    &--low { background: var(--vtm-warn); }
    &--empty { background: var(--vtm-crit); }
    &--unassigned,
    &--disabled { background: var(--vtm-ink-3); }
  }
  .aisles-card__fill {
    margin-top: 2px;
    width: 100%;
  }

  // ---- 货道弹出编辑器 ----
  .aisles-editor {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .aisles-editor__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .aisles-editor__title {
    font-family: var(--vtm-display);
    font-weight: 600;
    font-size: 13.5px;
    color: var(--vtm-heading);
  }
  .aisles-editor__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }
  .aisles-editor__label {
    font-size: 11px;
    font-weight: 600;
    color: var(--vtm-ink-2);
    // 按词换行，绝不从单词中间断开
    word-break: normal;
    overflow-wrap: normal;
    text-align: left;
    line-height: 1.3;
  }
  .aisles-editor__noproducts {
    padding: 10px 0 6px;
    text-align: center;
  }
  .aisles-editor__noproducts-text {
    font-size: 12px;
    color: var(--vtm-ink-3);
    margin-bottom: 2px;
  }
  .aisles-editor__static {
    // 与相邻的售价输入框对齐：同高、同内边距，只是不可编辑
    display: flex;
    align-items: center;
    height: 32px;
    padding: 0 10px;
    font-size: 13px;
    color: var(--vtm-ink-2);
    background: var(--vtm-surface-2);
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-sm);
  }
  .aisles-editor__preview ::v-deep .el-image {
    width: 60px;
    height: 60px;
    margin: 0 auto;
    display: block;
  }
  .aisles-editor__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  .aisles-editor__row--three {
    grid-template-columns: 1fr 1fr 1fr;

    // 三个标签长短不一（"Single Stock Warning" 要两行），不留出等高就会把
    // 第三个输入框顶下去，整行看着参差不齐
    .aisles-editor__label {
      min-height: 2.6em;
      display: flex;
      align-items: flex-start;
    }
  }
  .aisles-editor__switch {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
  .aisles-editor__foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--vtm-line);
  }

  // ---- 表格视图 ----
  .aisles__table-wrap {
    width: 100%;
  }
  // 表格视图：内容区不再自己滚动，由表格内部纵横滚动（表头固定，横向滚动条贴在可视区底部）
  .aisles__content--table {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    .aisles__table-wrap {
      flex: 1;
      min-height: 0;
    }
  }
  .aisles-table__img-fallback {
    width: 36px;
    height: 36px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--vtm-well);
    color: var(--vtm-ink-3);
    font-size: 14px;
  }
  ::v-deep .aisles-input--ok .el-input__inner { border-left: 3px solid var(--vtm-ok); }
  ::v-deep .aisles-input--low .el-input__inner { border-left: 3px solid var(--vtm-warn); }
  ::v-deep .aisles-input--empty .el-input__inner { border-left: 3px solid var(--vtm-crit); }
  ::v-deep .aisles-input--unassigned .el-input__inner,
  ::v-deep .aisles-input--disabled .el-input__inner { border-left: 3px solid var(--vtm-ink-3); }

  // ---- 拿货清单 ----
  .aisles-picklist {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .aisles-picklist__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .aisles-picklist__title {
    font-weight: 600;
    font-size: 13.5px;
    color: var(--vtm-heading);
  }
  .aisles-picklist__hint {
    font-size: 11.5px;
    color: var(--vtm-ink-3);
  }
  .aisles-picklist__empty {
    padding: 16px 0;
    text-align: center;
    font-size: 13px;
    color: var(--vtm-ink-3);
  }
  .aisles-picklist__list {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 280px;
    overflow-y: auto;
  }
  .aisles-picklist__item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 7px 0;
    border-bottom: 1px solid var(--vtm-line);

    &:last-child {
      border-bottom: none;
    }
  }
  .aisles-picklist__name {
    font-size: 12.5px;
    font-weight: 600;
    color: var(--vtm-ink);
  }
  .aisles-picklist__units {
    font-size: 12px;
    color: var(--vtm-mint-ink);
  }
  .aisles-picklist__slots {
    font-size: 11px;
    color: var(--vtm-ink-3);
  }

  // ---- 底部保存条 ----
  .aisles__footer {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 24px;
    border-top: 1px solid var(--vtm-line);
    background: var(--vtm-surface);
  }
  .aisles__footer-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .aisles__dirty {
    font-size: 12.5px;
    font-weight: 600;
    color: var(--vtm-mint-ink);
  }
  .aisles__footer-right {
    display: flex;
    gap: 8px;
  }

  .loading-option {
    text-align: center;
    font-size: 14px;
    padding: 0;
  }
  .loading-text {
    color: var(--vtm-ink-2);

    i {
      margin-right: 4px;
    }
  }
</style>
